import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import userModule from '@/store/user'
import DefaultContainer from '@/container/DefaultContainer.vue'
import Cookies from 'js-cookie'
import axios from 'axios'

Vue.use(VueRouter)

const ifNotAuthenticated = (to: any, from: any, next: any) => {
  const state = Cookies.get(process.env.VUE_APP_STATE_KEY!) as any
  if ((state && !JSON.parse(state).auth.token) || !state) {
    next()
    return
  }
  next('/')
}

const ifAuthenticated = (to: any, from: any, next: any) => {
  const state = Cookies.get(process.env.VUE_APP_STATE_KEY!) as any
  if (state && JSON.parse(state).auth.token) {
    axios.defaults.headers['Authorization'] = `Bearer ${
      JSON.parse(state).auth.token
    }`
    if (userModule.hasCategories || to.name === 'Interests') {
      // Only route to other pages than Interests if the user already has 6 interests/categories
      next()
      return
    }
    next('/interests')
    return
  }
  next('/login')
}

const persistAuthentication = (to: any, from: any, next: any) => {
  const state = Cookies.get(process.env.VUE_APP_STATE_KEY!) as any
  if (state && JSON.parse(state).auth.token) {
    axios.defaults.headers['Authorization'] = `Bearer ${
      JSON.parse(state).auth.token
    }`
  }
  next()
  return
}

const routes: Array<RouteConfig> = [
  {
    path: '/',
    redirect: '/home',
    name: 'HomeView',
    meta: {
      title: 'Home'
    },
    component: DefaultContainer,
    children: [
      {
        path: '/home',
        name: 'Home',
        meta: {
          title: 'Home'
        },
        component: () =>
          import(/* webpackChunkName: 'home' */ '../views/home/landing.vue'),
        beforeEnter: persistAuthentication
      },
      {
        path: 'home/lists/:cardListId',
        name: 'AllCards',
        component: () =>
          import(
            /* webpackChunkName: 'allCards' */ '../views/home/allCards.vue'
          ),
        beforeEnter: persistAuthentication
      },
      {
        path: 'home/cards/:cardId',
        name: 'HomeDeals',
        meta: {
          title: 'Deals'
        },
        component: () =>
          import(/* webpackChunkName: 'homeDeals' */ '../views/home/deals.vue'),
        beforeEnter: persistAuthentication
      },
      {
        path: '/deals/detail/:id',
        name: 'DealDetail',
        component: () =>
          import(
            /* webpackChunkName: 'dealDetail' */ '../views/deal/detail.vue'
          ),
        beforeEnter: persistAuthentication
      },
      {
        path: '/deals',
        name: 'Deals',
        meta: {
          title: 'Deals'
        },
        component: () =>
          import(/* webpackChunkName: 'deals' */ '../views/deal/allDeals.vue'),
        beforeEnter: persistAuthentication
      },
      {
        path: '/favorites',
        name: 'Favorites',
        meta: {
          title: 'Favoriten'
        },
        component: () =>
          import(/* webpackChunkName: 'favorites' */ '../views/favorites.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/profile',
        name: 'Profile',
        meta: {
          title: 'Mein Profil'
        },
        component: () =>
          import(/* webpackChunkName: 'profile' */ '../views/profile.vue'),
        beforeEnter: ifAuthenticated
      },
      {
        path: '/settings',
        name: 'Settings',
        meta: {
          title: 'Einstellungen'
        },
        component: () =>
          import(/* webpackChunkName: 'settings' */ '../views/settings.vue'),
        beforeEnter: ifAuthenticated
      }
    ]
  },
  {
    path: '/login',
    name: 'Login',
    meta: {
      title: 'Anmelden'
    },
    component: () =>
      import(/* webpackChunkName: 'login' */ '../views/login.vue'),
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/accounts/password/reset',
    name: 'PasswordReset',
    meta: {
      title: 'Passwort vergessen?'
    },
    component: () =>
      import(/* webpackChunkName: 'login' */ '../views/login.vue'),
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/accounts/password/new/:token',
    name: 'NewPassword',
    meta: {
      title: 'Neues Passwort wählen'
    },
    component: () =>
      import(/* webpackChunkName: 'login' */ '../views/login.vue'),
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/register',
    name: 'Register',
    meta: {
      title: 'Registrierung'
    },
    component: () =>
      import(/* webpackChunkName: 'register' */ '../views/register.vue'),
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/confirm/:token',
    name: 'Confirm',
    meta: {
      title: 'Account bestätigen'
    },
    component: () =>
      import(/* webpackChunkName: 'confirm' */ '../views/confirm.vue')
  },
  {
    path: '/interests',
    name: 'Interests',
    meta: {
      title: 'Interessen'
    },
    component: () =>
      import(/* webpackChunkName: 'interests' */ '../views/interests.vue'),
    beforeEnter: ifAuthenticated
  },
  {
    path: '/imprint',
    name: 'Imprint',
    meta: {
      title: 'Impressum'
    },
    component: () =>
      import(/* webpackChunkName: 'imprint' */ '../views/util/markdown.vue')
  },
  {
    path: '/terms',
    name: 'Terms',
    meta: {
      title: 'AGB'
    },
    component: () =>
      import(/* webpackChunkName: 'terms' */ '../views/util/markdown.vue')
  },
  {
    path: '/usage',
    name: 'Usage',
    meta: {
      title: 'Nutzungsbedingungen'
    },
    component: () =>
      import(/* webpackChunkName: 'usage' */ '../views/util/markdown.vue')
  },
  {
    path: '/privacy',
    name: 'Privacy',
    meta: {
      title: 'Datenschutz'
    },
    component: () =>
      import(/* webpackChunkName: 'privacy' */ '../views/util/markdown.vue')
  },
  {
    path: '/faq',
    name: 'FAQ',
    meta: {
      title: 'FAQ'
    },
    component: () =>
      import(/* webpackChunkName: 'faq' */ '../views/util/markdown.vue')
  },
  {
    path: '/press',
    name: 'Press',
    meta: {
      title: 'Presse'
    },
    component: () =>
      import(/* webpackChunkName: 'press' */ '../views/util/markdown.vue')
  },
  {
    path: '/jobs',
    name: 'Jobs',
    meta: {
      title: 'Jobs'
    },
    component: () =>
      import(/* webpackChunkName: 'imprint' */ '../views/util/markdown.vue')
  },
  {
    path: '*',
    redirect: '/home'
  }
]

const router = new VueRouter({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 }
  },
  routes
})

router.beforeEach((to, from, next) => {
  const newTitle = to.meta?.title
  document.title =
    typeof newTitle === 'string' ? `Münsterland.de - ${newTitle}` : 'Münsterland.de'
  next()
})

axios.defaults.baseURL = process.env.VUE_APP_BASE_URL
axios.defaults.headers['content-Type'] = 'application/json'

export default router
